import { Box, Button, Typography } from "@mui/material";
import { ModelRenderer } from "../ModelRenderer";
import { PropValueLine } from "../common/PropValueLine";
import { useEffect, useState } from "react";
import * as THREE from "three";
import { CollisionBox, Model } from "../../models/Model";
import { MLEvents } from "../common/ModelLoader";
import { Vec3Editor } from "../common/Vec3Editor";
import { copyVec3 } from "../../visuals/visual-utils";


export function CollisionMapPanel({ mr }: { mr: ModelRenderer }) {
  const [model, setModel] = useState<Model | null>(null);
  useEffect(() => {
    // Effect logic here
    const handleModelLoaded = (m?: THREE.Object3D) => {
      setModel(mr.loader.model);
    };
    handleModelLoaded(mr.loader.modelObj);
    mr.loader.onEvent(MLEvents.ModelLoaded, handleModelLoaded);
    return () => {
      mr.loader.removeListener(MLEvents.ModelLoaded, handleModelLoaded);
    };

  }, [mr]);


  const mergeBox = (u: Partial<CollisionBox>, index: number) => {
    if (!model) return;
    const b = model.collisionMap.boxes[index];
    Object.assign(b, u);
    setModel({ ...model });
  }

  if (!model) return null;

  function generateOne() {
    if (!model) return;
    const cm = model.collisionMap;
    const cb: CollisionBox = {
      position: { x: 0, y: 0, z: 0 },
      rotation: { x: 0, y: 0, z: 0 },
      size: { x: 0, y: 0, z: 0 }
    };
    cm.boxes = [cb];
    let obj = mr.loader.getContainer();
    const bbox = new THREE.Box3().setFromObject(obj);
    const size = bbox.getSize(new THREE.Vector3());
    const position = bbox.getCenter(new THREE.Vector3());
    copyVec3(size, cb.size);
    copyVec3(position, cb.position);
    setModel({ ...model });
    mr.loader.model.collisionMap = cm;
    visualize(true);
    // const rotation = new THREE.Euler().setFromQuaternion(container.quaternion);
    // mergeBox({ position, size }, 0);
  }

  function generateMany() {
    if (!model) return;
    const cm = model.collisionMap;
    cm.boxes = [];
    const outerBox: CollisionBox = {
      position: { x: 0, y: 0, z: 0 },
      rotation: { x: 0, y: 0, z: 0 },
      size: { x: 0, y: 0, z: 0 }
    };
    let obj = mr.loader.getContainer();
    const bbox = new THREE.Box3().setFromObject(obj);
    const size = bbox.getSize(new THREE.Vector3());
    const position = bbox.getCenter(new THREE.Vector3());
    copyVec3(size, outerBox.size);
    copyVec3(position, outerBox.position);
    const resolution = 0.75;
    for (let i = 0; i <= outerBox.size.x; i += resolution) {
      for (let j = 0; j <= outerBox.size.y; j += resolution) {
        const poker = new THREE.Raycaster();
        const origin = new THREE.Vector3(i,j,outerBox.size.z + 1).add(bbox.min);
        const direction = new THREE.Vector3(0, 0, -1);
        poker.set(origin, direction);
        const intersects = poker.intersectObject(obj, true);
        if (intersects.length > 0) {
          const point = intersects[0].point;
          const cb: CollisionBox = {
            position: { x: origin.x, y: origin.y, z: point.z / 2 },
            rotation: { x: 0, y: 0, z: 0 },
            size: { x: resolution, y: resolution, z: point.z }
          };
          cm.boxes.push(cb);
        }
      }
    }
    setModel({ ...model });
    mr.loader.model.collisionMap = cm;
    visualize(true);
    // const rotation = new THREE.Euler().setFromQuaternion(container.quaternion);
    // mergeBox({ position, size }, 0);
  }


  function visualize(on: boolean) {
    let collisionMapObj = mr.loader.getContainer().getObjectByName('collisionMap');
    if (!collisionMapObj) {
      collisionMapObj = new THREE.Group();
      collisionMapObj.name = 'collisionMap';
      mr.loader.getContainer().add(collisionMapObj);
    }
    collisionMapObj.clear();
    if (!on) return;
    const model = mr.loader.model;
    const cm = model.collisionMap;
    for (const box of cm.boxes) {
      const boxMesh = new THREE.Mesh(
        new THREE.BoxGeometry(box.size.x, box.size.y, box.size.z),
        new THREE.MeshBasicMaterial({ wireframe: true })
      );
      boxMesh.position.copy(box.position);
      boxMesh.name = `box_${cm.boxes.indexOf(box)}`;
      collisionMapObj.add(boxMesh);
    }
  }

  return (
    <Box>
      {model.collisionMap.boxes.map((box, index) => (
        <Box key={index}>
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            {`Box ${index + 1}`}
          </Typography>
          <PropValueLine label='Position' value={
            <Vec3Editor value={box.position} onChange={v => mergeBox({ position: v }, index)} />
          } />
          {/* <PropValueLine label='Rotation' value={
            <Vec3Editor value={box.rotation} onChange={v => merge({ rotation: v }, index)} />
          } /> */}
          <PropValueLine label='Size' value={
            <Vec3Editor value={box.size} onChange={v => mergeBox({ size: v }, index)} />
          } />
        </Box>
      ))}
      <Box sx={{ marginTop: 2 }}>
        <Button onClick={() => visualize(true)} color='secondary'>
          Visualize
        </Button>

        <Button onClick={generateOne} color='secondary'>
          Generate 1
        </Button>

        <Button onClick={generateMany} color='secondary'>
          Generate Many
        </Button>

      </Box>
    </Box>
  );
}
